import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import classes from "./FocusCarousel.module.css";

const FocusCarousel = ({ children, itemSize = 110, activeScale = 1.55, autoPlay = true, interval = 2500, className = "" }) => {
    const trackRef = useRef(null);
    const frameRef = useRef(null);
    const slides1 = React.Children.toArray(children).filter(Boolean);
    const middleOriginalIndex = slides1.length > 0 ? Math.floor((slides1.length - 1) / 2) : 0;
    const initialFocusIndex =
        slides1.length >= 5
            ? slides1.length + middleOriginalIndex
            : middleOriginalIndex;
    const [focusIndex, setFocusIndex] = useState(initialFocusIndex);
    const [isPaused, setIsPaused] = useState(false);
    const slides =
        slides1.length >= 5
            ? [...slides1, ...slides1, ...slides1]
            : slides1;
    const trackClassName = `${classes.Track}${slides.length === 1 ? ` ${classes.TrackSingle}` : ""}`;

    const scrollToItem = (index, behavior = "auto") => {
        const track = trackRef.current;
        if (!track) return;
        const items = track.querySelectorAll("[data-carousel-item]");
        const item = items[index];
        if (!item) return;
        const itemCenter = item.offsetLeft + item.offsetWidth / 2;
        track.scrollTo({ left: itemCenter - track.clientWidth / 2, behavior });
    };

    const updateFocusIndex = () => {
        const track = trackRef.current;
        if (!track || slides.length <= 1)
            return;
        const items = track.querySelectorAll("[data-carousel-item]");
        const center = track.scrollLeft + track.clientWidth / 2;
        let closestIndex = 0;
        let closestDistance = Infinity;
        items.forEach((item, index) => {
            const itemCenter = item.offsetLeft + item.offsetWidth / 2;
            const distance = Math.abs(center - itemCenter);
            if (distance < closestDistance) {
                closestDistance = distance;
                closestIndex = index;
            }
        });
        setFocusIndex((prev) => (prev === closestIndex ? prev : closestIndex));
    };
    const handleScroll = () => {
        if (frameRef.current) return;
        frameRef.current = requestAnimationFrame(() => {
            frameRef.current = null;
            updateFocusIndex();
        });
    };

    useLayoutEffect(() => {
        if (slides1.length <= 1) return;
        scrollToItem(initialFocusIndex);
        setFocusIndex(initialFocusIndex);
    }, [slides1.length, initialFocusIndex]);

    useEffect(() => {
        updateFocusIndex();
    }, [slides.length]);

    useEffect(() => {
        return () => {
            if (frameRef.current) cancelAnimationFrame(frameRef.current);
        };
    }, []);

    useEffect(() => {
        if (!autoPlay || isPaused || slides1.length < 3) return;
        const track = trackRef.current;
        if (!track) return;
        const id = setInterval(() => {
            const maxScroll = track.scrollWidth - track.clientWidth;
            if (track.scrollLeft >= maxScroll - itemSize) {
                scrollToItem(initialFocusIndex);
            } else {
                track.scrollBy({ left: itemSize, behavior: "smooth" });
            }
        }, interval);

        return () => clearInterval(id);
    }, [autoPlay, isPaused, interval, itemSize, slides1.length, initialFocusIndex]);

    return (
        <div className={`${classes.FocusCarousel} ${className}`} style={{ "--item-size": `${itemSize}px`, "--active-scale": activeScale }}>
            <div ref={trackRef} className={trackClassName} onScroll={handleScroll} onMouseEnter={() => setIsPaused(true)} onMouseLeave={() => setIsPaused(false)}>
                <div className={classes.Spacer} aria-hidden="true" />

                {slides.map((child, index) => (
                    <div key={`${child.key ?? "item"}-${index}`} data-carousel-item={index} className={`${classes.Item} ${index === focusIndex ? classes.ItemActive : ""}`}>
                        <div className={classes.ItemContent}>{child}</div>
                    </div>
                ))}

                <div className={classes.Spacer} aria-hidden="true" />
            </div>
        </div>
    );
};

export default FocusCarousel;
